import { useCommentsStore } from "@/app/event-detail/engagement/comments/store";
import EngagementSection from "@/app/event-detail/engagement/engagement";
import { useTitleCompact } from "@/app/event-detail/hooks/use-title-compact";
import { ChevronRightIcon } from "@/components/event-detail/chevron-right-icon";
import { OrderHistorySheet } from "@/components/event-detail/order-history-sheet";
import { PositionsPanel } from "@/components/event-detail/positions-panel";
import { RulesSection } from "@/components/event-detail/rules-section";
import { TradeButtonBar } from "@/components/event-detail/trade-button-bar";
import { OrderBook } from "@/components/trade-panel/components/order-book";
import { CryptoEventViewModel } from "@/event-core/types";
import { cn } from "@/lib/utils";
import { useAppRouter } from "@/utils/router";
import { get } from "@/utils/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CryptoEventOverview } from "./crypto-event-overview";
import { useGuessList } from "./hooks/use-guess-list";
import { useSelectedGuess } from "./hooks/use-selected-guess";

function getEngagementGuessTitle( guess: { title?: string } ): string {
    const title = get( guess, "title", "" );
    const groupTitle = get( guess, "groupTitle", "" );
    return groupTitle ? `${groupTitle} · ${title}` : title;
}

export default function Detail( { data }: { data: CryptoEventViewModel } ) {
    const router = useAppRouter();
    const [ orderSheetOpen, setOrderSheetOpen ] = useState( false );
    const titleCompactProgress = useTitleCompact( 40, 160 );

    const { guessList, updateGuessList } = useGuessList( data );
    const { selectedGuessNo, selectGuess: guess, setSelectedGuessNo } = useSelectedGuess( guessList );
    const enrichUserGuessOptionsWithTitles = useCommentsStore( ( state ) => state.enrichUserGuessOptionsWithTitles );
    const event = data.eventData || {};

    const guessListForEngagement = useMemo(
        () =>
            guessList.map( ( guess ) => ( {
                guessNo: guess.guessNo,
                title: getEngagementGuessTitle( guess ),
                mainFlag: guess.mainFlag,
                yesDesc: guess.yesDesc,
                noDesc: guess.noDesc,
            } ) ),
        [ guessList ],
    );

    useEffect( () => {
        if ( guessListForEngagement.length > 0 ) {
            enrichUserGuessOptionsWithTitles( guessListForEngagement );
        }
    }, [ guessListForEngagement, enrichUserGuessOptionsWithTitles ] );

    const isPreview = false; // logic for preview mode if needed

    const handleOpenOrderHistory = useCallback(
        ( _guessNo: string, _direction: number, _betType: number, _price: number ) => {
            setOrderSheetOpen( true );
        },
        [],
    );
    // 计算持仓情况
    const userYesShareQuantity = guess.userYesShareQuantity;
    const userNoShareQuantity = guess.userNoShareQuantity;
    const hasSellableShares = userYesShareQuantity > 0 || userNoShareQuantity > 0;
    const handleTransaction = () => {
        const eventNo = data.eventNo || "";
        router.push( `/order-guess-detail?guessno=${guess.guessNo}&eventno=${eventNo}` );
    };

    return (
        <div className="flex flex-col flex-1 min-h-0 h-full relative">
            <div className="event-detail-scrollable min-h-0 flex flex-col flex-1 h-full overflow-y-auto scrollbar-hide">
                {/* Header */}
                <CryptoEventOverview
                    event={event}
                    guess={guess}
                    guessList={guessList}
                    isSingleGuessDetailPage={false}
                    isInsetLayout={false}
                    titleCompactProgress={titleCompactProgress}
                    onBack={router.back}
                    onGuessChange={( guess ) => setSelectedGuessNo( guess )}
                    onUpdateGuessList={updateGuessList}
                />
                <div className="rounded-xl border border-stroke bg-surface-card px-3 h-12" onClick={handleTransaction}>
                    <button type="button" className={cn( "flex w-full items-center justify-between py-3" )}>
                        <span className="flex items-center gap-1">
                            <span className="text-base font-medium text-t1">My Transactions</span>
                        </span>
                        <ChevronRightIcon className={`w-6 h-6 text-t3 transition-transform duration-200`} />
                    </button>
                </div>
                {guess.status === 1 && (
                    <OrderBook
                        guessInfo={guess}
                        eventNo={event.eventNo}
                        defaultExpanded={true}
                        className="rounded-xl border border-stroke bg-surface-card px-3 mt-3"
                    >
                    </OrderBook>
                )}
                <PositionsPanel
                    event={{
                        ...event,
                        eventNo: data.eventNo ?? get( event, "eventNo", "" ),
                        eventTitle: data.eventData?.eventTitle,
                        eventImageUrl: data.eventData?.eventImageUrl,
                        guessList,
                    }}
                    guessNo={guess.guessNo}
                />
                <RulesSection event={data.eventData} showSourceRect={true} />

                <EngagementSection
                    eventNo={data.eventNo || ""}
                    eventData={data.eventData}
                    guessList={guessListForEngagement}
                />
                <OrderHistorySheet
                    open={orderSheetOpen}
                    onOpenChange={setOrderSheetOpen}
                    guessNo={guess.guessNo}
                    event={event}
                    guess={guess}
                />
            </div>
            {guess.status === 1 && (
                <div className="w-100 -mx-4 bg-surface-card">
                    <TradeButtonBar
                        guess={guess}
                        event={event}
                        hasSellableShares={hasSellableShares}
                        onRefreshHoldings={() => {}}
                    />
                </div>
            )}
        </div>
    );
}
